/**
 * Brand Configuration Service
 *
 * This service manages brand-specific configuration (Mobiliar vs Protekta)
 * It can detect the brand at runtime based on hostname or build-time configuration
 */

import { Injectable, inject, isDevMode, signal } from '@angular/core';

import { AppSource, MOBILIAR, PROTEKTA } from '../common.constant';
import { LOCAL_BRAND, WINDOW } from '../core/provide-oiv-core.provider';

export interface BrandConfig {
    /** Brand identifier */
    name: 'mobiliar' | 'protekta';

    /** App source constant (maps to MOBILIAR or PROTEKTA) */
    appSource: AppSource;

    /** Primary domain for this brand */
    domain: string;

    /** Custom domain prefix (for RWC configuration) */
    customDomainPrefix?: string;

    /** Translation key for brand name (e.g., 'oiv.app.mobiliar') */
    translationBrandKey: string;

    /** Favicon source: 'mobiliar' | 'protekta' | undefined */
    faviconSource?: 'mobiliar' | 'protekta';

    /** All custom domains for this brand */
    customDomains: string[];
}

@Injectable({
    providedIn: 'root',
})
export class BrandConfigService {
    #window = inject(WINDOW);
    #localBrand = inject(LOCAL_BRAND);
    #brandConfig = signal<BrandConfig>(this.#detectBrand());

    #detectBrand(): BrandConfig {
        if (isDevMode() && this.#localBrand) {
            return BRAND_CONFIGS[this.#localBrand];
        }

        const hostname = this.#window.location.hostname;

        if (this.#isProtektaDomain(hostname)) {
            return BRAND_CONFIGS['protekta'];
        }

        return BRAND_CONFIGS['mobiliar'];
    }

    #isProtektaDomain(hostname: string): boolean {
        // Single source of truth: BRAND_CONFIGS.protekta.customDomains (includes prod and preprod domains).
        return BRAND_CONFIGS['protekta'].customDomains.some(domain => hostname.includes(domain));
    }

    getBrand(): BrandConfig {
        return this.#brandConfig();
    }

    getBrandByName(brandName: 'mobiliar' | 'protekta'): BrandConfig {
        return BRAND_CONFIGS[brandName];
    }

    isProtekta(): boolean {
        return this.#brandConfig().name === 'protekta';
    }

    isMobiliar(): boolean {
        return this.#brandConfig().name === 'mobiliar';
    }
}

/**
 * Brand configurations
 * This object contains all supported brand configurations
 */
export const BRAND_CONFIGS: Record<'mobiliar' | 'protekta', BrandConfig> = {
    mobiliar: {
        name: 'mobiliar',
        appSource: MOBILIAR,
        domain: 'oiv.mobiliar.ch',
        customDomainPrefix: 'oiv',
        translationBrandKey: 'oiv.app.mobiliar',
        faviconSource: 'mobiliar',
        customDomains: [
            'oiv.mobiliar.ch',
            'oiv.mobiliare.ch',
            'oiv.mobiliere.ch',
            'oiv.mobiliar-preprod.ch',
            'oiv.mobiliare-preprod.ch',
            'oiv.mobiliere-preprod.ch',
        ],
    },
    protekta: {
        name: 'protekta',
        appSource: PROTEKTA,
        domain: 'oiv.protekta.ch',
        customDomainPrefix: 'b2e.oiv',
        translationBrandKey: 'oiv.app.protekta',
        faviconSource: 'protekta',
        customDomains: [
            'oiv.protekta.ch',
            'oiv.protekta-preprod.ch',
            'oiv-protekta.mobiliar.ch',
            'oiv-protekta.mobiliar-preprod.ch',
            'oiv-protekta.mobiliere.ch',
            'oiv-protekta.mobiliere-preprod.ch',
            'oiv-protekta.mobiliare.ch',
            'oiv-protekta.mobiliare-preprod.ch',
            'b2e.oiv.protekta.ch',
            'b2e.oiv.protekta-preprod.ch',
            'b2e.oiv-protekta.mobiliar.ch',
            'b2e.oiv-protekta.mobiliar-preprod.ch',
        ],
    },
};
